/**
 * Design System - IconButton Component
 * Reusable icon button for navigation and actions
 */

import React from 'react';
import { backgroundColors, spacing, borders, dimensions } from '../../utils';

export interface IconButtonProps {
  icon: React.ReactNode;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'ghost';
  ariaLabel?: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  onClick,
  size = 'md',
  variant = 'default',
  ariaLabel,
}) => {
  const sizeConfig = {
    sm: {
      size: dimensions.component.icon.sm,
      padding: spacing[1],
    },
    md: {
      size: dimensions.component.iconButton.width,
      padding: spacing[2],
    },
    lg: {
      size: dimensions.component.icon.lg,
      padding: spacing[3],
    },
  };

  const config = sizeConfig[size];
  const buttonSize = config.size;

  const getBackgroundColor = () => {
    if (variant === 'ghost') return 'transparent';
    if (variant === 'outlined') return 'transparent';
    return backgroundColors.primary;
  };

  const getBorderColor = () => {
    if (variant === 'ghost') return 'transparent';
    return backgroundColors.shade;
  };

  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        width: buttonSize,
        height: buttonSize,
        backgroundColor: getBackgroundColor(),
        borderColor: getBorderColor(),
        borderWidth:
          variant === 'ghost' ? borders.width.none : borders.width.thin,
        borderStyle: 'solid',
        borderRadius: borders.radius.full,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        padding: config.padding,
      }}
      onMouseEnter={e => {
        if (onClick) {
          e.currentTarget.style.backgroundColor =
            variant === 'ghost' || variant === 'outlined'
              ? backgroundColors.secondary
              : backgroundColors.secondary;
        }
      }}
      onMouseLeave={e => {
        if (onClick) {
          e.currentTarget.style.backgroundColor = getBackgroundColor();
        }
      }}
    >
      {icon}
    </button>
  );
};
